import { Injectable, inject } from '@angular/core';
import * as _ from 'lodash-es';
import { GeneralError } from '@feathersjs/errors';
import { ProofreadingService } from './proofreading.service';
import { AuthService } from './auth.service';
import { Member, MemberPermission, MemberStatus } from '../models/projects/submodels/members.model';
// import {BehaviorSubject} from 'rxjs/BehaviorSubject';

// @ts-ignore
// const api = require('@modules/cf-api.js').api;;
// const api_async = require('@modules/cf-api.js').api_async;

@Injectable({
    providedIn: 'root',
})
export class MembersService {
    protected s_auth = inject(AuthService);
    protected s_proofreadings = inject(ProofreadingService);

    public getMembers(scope: 'extern' | 'intern' = 'extern') {
        return scope === 'intern'
            ? this.s_proofreadings.current?.intern?.approval?.members || []
            : this.s_proofreadings.current?.members || [];
    }

    get members() {
        return this.getMembers();
    }

    public getCurrentMember(scope: 'extern' | 'intern' = 'extern') {
        const userID = this.s_auth.user?._id?.toString();
        return this.getMembers(scope).find(member => {
            return !!userID && member.usersID.map(id => id.toString()).includes(userID);
        });
    }

    get current_member() {
        return this.getCurrentMember();
    }

    public isApproverDocumentFor(scope: 'extern' | 'intern' = 'extern'): boolean {
        return !!this.getCurrentMember(scope)?.hasApproDocument;
    }

    public isApproverInfoFor(scope: 'extern' | 'intern' = 'extern'): boolean {
        return !!this.getCurrentMember(scope)?.hasApproInfo;
    }

    public hasDecisionFor(scope: 'extern' | 'intern' = 'extern'): boolean {
        return this.s_auth.isCPP()
            || this.s_auth.isOperator()
            || this.s_auth.isFab()
            || this.isApproverDocumentFor(scope)
            || this.isApproverInfoFor(scope);
    }

    // CURRENT MEMBER PRIVILEGES
    get isApproverDocument(): boolean {
        return this.isApproverDocumentFor();
    }
    get isApproverInfo(): boolean {
        return this.isApproverInfoFor();
    }
    get hasDecision(): boolean {
        return this.hasDecisionFor();
    }

    public getMembersByPerms(permsRequested: MemberPermission[], scope: 'extern' | 'intern' = 'extern') {
        return this.getMembers(scope).filter(member => this.hasPermission(permsRequested, '&', member.permissions));
    }

    public addMainApprover(approver: Member, members: Member[]) {
        const current_main_index = members.findIndex(m => m.hasApproDocument);
        if (current_main_index !== -1) {
            approver.permissions = _.uniq([MemberPermission.APPROVAL, ...approver.permissions]);
            members[current_main_index] = approver;
        }
        else {
            members.push(approver);
        }
        return _.uniqWith([approver, ...members], (a, b) => _.isEqual(a.usersID[0], b.usersID[0]));
    }

    public hasPermission(permsRequested: MemberPermission[], operator: string, user_perms?: string[]) {
        if (operator !== '&' && operator !== '|') {
            throw new GeneralError('NO operator set !');
        }
        const perms = user_perms || this.current_member?.permissions || [];
        return operator === '&' ? _.every(permsRequested, perm => _.includes(perms, perm)) : _.some(permsRequested, perm => _.includes(perms, perm));
    }

    public hasStatus(statusRequested: string[], operator: string, scope: 'extern' | 'intern' = 'extern') {
        if (operator !== '&' && operator !== '|') {
            throw new GeneralError('NO operator set !');
        }
        const status = this.getCurrentMember(scope)?.status || MemberStatus.PENDING;
        return operator === '&' ? _.every(statusRequested, st => st.includes(status)) : _.some(statusRequested, st => st.includes(status));
    }
}
